import React from "react";
import { MapPin, Building2 } from "lucide-react";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { PageHero, SectionHeading, CtaBanner } from "@/components/marketing/blocks";
import { useSeo } from "@/hooks/useSeo";

const COMMUNITIES = [
  "Memphis",
  "Bartlett",
  "Germantown",
  "Collierville",
  "Cordova",
  "Millington",
  "Arlington",
  "Lakeland",
];

export default function ServiceAreas() {
  const origin = "https://artadmin.net";
  useSeo({
    title: "Service Areas — Memphis & Shelby County | AR Process Service and Notary",
    description:
      "AR Process Service provides process serving and notary services throughout the Memphis metro area and Shelby County, Tennessee.",
    canonicalPath: "/service-areas",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Process Serving and Notary Services",
      provider: { "@type": "LegalService", "@id": "https://artadmin.net/#business", name: "AR Process Service and Notary" },
      areaServed: [
        { "@type": "City", name: "Memphis" },
        { "@type": "AdministrativeArea", name: "Shelby County, Tennessee" },
      ],
      url: `${origin}/service-areas`,
    },
  });

  return (
    <MarketingLayout>
      <PageHero
        overline="Coverage"
        title="Service Areas — Memphis Metro &amp; Shelby County"
        subtitle="We serve the greater Memphis metropolitan area and Shelby County, Tennessee, including the surrounding municipalities below."
      />

      <section className="px-5 py-14 sm:px-8 lg:py-20">
        <SectionHeading overline="Where we work" title="Communities we serve" />
        <div className="mx-auto mt-10 max-w-5xl rounded-2xl border border-slate-200 bg-white p-7">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50">
              <Building2 className="h-6 w-6 text-sky-700" />
            </div>
            <h3 className="font-display text-lg font-bold text-slate-900 sm:text-xl">Memphis Metro &amp; Shelby County, TN</h3>
          </div>
          <ul className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {COMMUNITIES.map((c) => (
              <li key={c} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-[15px] font-medium text-slate-800">
                <MapPin className="h-5 w-5 shrink-0 text-sky-500" /> {c}
              </li>
            ))}
          </ul>
        </div>
        <p className="mx-auto mt-8 max-w-3xl text-center text-[15px] text-slate-500">
          Don't see your community?{" "}
          <a href="tel:+19016952500" className="font-semibold text-sky-700 hover:underline">Call (901) 695-2500</a> to confirm coverage in the Memphis metro area.
        </p>
      </section>

      <section className="border-y border-slate-200 bg-white px-5 py-14 sm:px-8 lg:py-20">
        <SectionHeading
          overline="Turnaround"
          title="Timely service across the metro"
          subtitle="Turnaround depends on address details and urgency. Rush and 24-Hour Priority options are available for time-sensitive matters within the Memphis metro area."
        />
      </section>

      <CtaBanner title="In the Memphis metro area?" subtitle="Start a service request and we'll get to work across Shelby County." />
    </MarketingLayout>
  );
}
